import { LoanApplicationEntities as InsuranceApplicationEntities } from 'commonui';
import { IGoogleAnalytics } from 'types/customTypes';

/**
 * This interface is for analytics events that are provided in the Product
 * Configuration within the Product Config Generator. They are mapped from
 * the Insurance app External app response and then utilize as part of the input
 * field changes (whenever they are changed).
 */
export interface IAnalyticsDataField extends InsuranceApplicationEntities.IDataField {
    googleAnalytics: IGoogleAnalytics;
}

/**
 * A list of all the events that are tracked in google analytics. This includes
 * those that are defined in the product config generator's data field for
 * googleAnalytics, so even if it isn't used explicitly in code, it may be used
 * inside of PCG (this is the case for INPUT and SELECTION).
 */
export enum AnalyticsEvent {
    ADDRESS_SCRUBBING = 'addressScrubbing',
    ADTRAX = 'adTrax',
    APPLICATION_FUNNEL = 'DLVPage',
    CHECKBOX_INTERACTION = 'trackCheckInteraction',
    HEALTH_QUESTIONS = 'trackHealthQuestions',
    HELP_TEXT = 'trackContextualHelp',
    INPUT = 'trackInput',
    OTHER_BUTTONS = 'trackOtherButtons',
    SELECTION = 'trackSelection',
    FIELD_ERROR = 'formFieldError',
    ERROR_BANNER = 'trackErrorBanner',
    ALERT_BANNER = 'trackWarningBanner',
    RIDER_DISPLAYED = 'riderDisplayed',
    RIDER_DISPLAYED_SELECTED = 'riderDisplayedSelected',
    RIDER_SELECTED = 'riderSelected',
    RIDER_REMOVED = 'riderRemoved',
    USER_DATA = 'userData',
    ERROR_LOADING_DOC = 'errorLoadingSignDoc',
    PROGRESS_ITEM_LIST_BUTTON = 'trackNavigationButtons',
    ABANDON_APP = 'abandonApp',
    PIXEL_FIRE = 'keplerEvent',
    SAFETY_NET_TIMER_DISPLAYED = 'safetyNetTimerDisplayed',
    SAFETY_NET_TIMER_UPDATED = 'safetyNetTimerUpdated'
}

/*
 * The following interfaces that start with IAnalytics are all of the data that
 * we utilize intermittently before we send the data to google analytics. The
 * event name is tied together in a CustomEvent when we fire the event to be
 * picked up in the AnalyticsService. Most of the fields are what they are when
 * we send them to GA; however, segmentTitle gets mapped to stepName and some of
 * the field names may have a period separator in them.
 */

export interface IAnalyticsBase {
    channel: string;
    segmentTitle: string;
    productCode: string;
    premiumAmount: string;
    coverageAmount: string;
}

export interface IAnalyticsApplication extends IAnalyticsBase {
    policyStatus?: string;
}

export interface IAnalyticsProductNotAvailable extends IAnalyticsBase {
    pnaReason: string;
}

export interface IAnalyticsAppInputChange extends IAnalyticsBase {
    policyStatus?: string;
    fieldName: string;
    fieldAction: string;
    riderProductCode?: string;
    riderPremiumAmount?: string;
    riderCoverageAmount?: string;
}

export interface IAnalyticsAdTrax {
    adTraxValue: string;
}

export interface IAnalyticsAppAddressScrubbing {
    action: string;
    label: string;
}

export interface IAnalyticsFieldError extends IAnalyticsBase {
    policyStatus?: string;
    fieldName: string;
    errorValue: string;
}

export interface IAnalyticsBanner extends IAnalyticsBase {
    policyStatus?: string;
    bannerMessage: string;
    fieldAction?: string;
}

export interface IAnalyticsRiderDisplay {
    segmentTitle: string;
    productCode: string;
    premiumAmount: string;
    coverageAmount: string;
}

export interface IAnalyticsUserData {
    [key: string]: string;
}

export interface IAnalyticsPixelFire {
    spotName: string;
    productCode: string;
}
declare global {
    /**
     * A mapping between the event names and the custom event. This is used to
     * retrieve the event information based on the analytics event key.
     */
    interface GlobalEventHandlersEventMap {
        [AnalyticsEvent.ADDRESS_SCRUBBING]: CustomEvent;
        [AnalyticsEvent.ADTRAX]: CustomEvent;
        [AnalyticsEvent.APPLICATION_FUNNEL]: CustomEvent;
        [AnalyticsEvent.CHECKBOX_INTERACTION]: CustomEvent;
        [AnalyticsEvent.HEALTH_QUESTIONS]: CustomEvent;
        [AnalyticsEvent.HELP_TEXT]: CustomEvent;
        [AnalyticsEvent.INPUT]: CustomEvent;
        [AnalyticsEvent.OTHER_BUTTONS]: CustomEvent;
        [AnalyticsEvent.SELECTION]: CustomEvent;
        [AnalyticsEvent.FIELD_ERROR]: CustomEvent;
        [AnalyticsEvent.ERROR_BANNER]: CustomEvent;
        [AnalyticsEvent.ALERT_BANNER]: CustomEvent;
        [AnalyticsEvent.USER_DATA]: CustomEvent;
        [AnalyticsEvent.RIDER_DISPLAYED]: CustomEvent;
        [AnalyticsEvent.RIDER_DISPLAYED_SELECTED]: CustomEvent;
        [AnalyticsEvent.RIDER_SELECTED]: CustomEvent;
        [AnalyticsEvent.RIDER_REMOVED]: CustomEvent;
        [AnalyticsEvent.ERROR_LOADING_DOC]: CustomEvent;
        [AnalyticsEvent.ABANDON_APP]: CustomEvent;
        [AnalyticsEvent.PROGRESS_ITEM_LIST_BUTTON]: CustomEvent;
        [AnalyticsEvent.PIXEL_FIRE]: CustomEvent;
        [AnalyticsEvent.SAFETY_NET_TIMER_DISPLAYED]: CustomEvent;
        [AnalyticsEvent.SAFETY_NET_TIMER_UPDATED]: CustomEvent;
    }
}

/*
 * All of the following interfaces that start with IGTM are used for tests.
 * However, these are the interfaces that we would *actually* send to google
 * analytics. In essence, the analytics event gets tacked on to the IAnalytics
 * correlating interface that would then get properly mapped and sent to google
 * analytics. This is wrapped in the IGTMPayload interface, which is the final
 * wrapper we use to send to the TagManager.
 */

export interface IGTMApplication {
    event: AnalyticsEvent;
}

export interface IGTMAdvancedApplication extends IGTMApplication {
    stepName: string;
    channel: string;
    productCode: string;
    premiumAmount: string;
    coverageAmount: string;
}

export interface IGTMApplicationFunnel extends IGTMAdvancedApplication {
    policyStatus?: string;
}

export interface IGTMAdTrax extends IGTMApplication {
    adTraxValue: string;
}

export interface IGTMAppInputChange extends IGTMAdvancedApplication {
    'fieldName': string;
    'fieldAction': string;
    'rider.productCode'?: string;
    'rider.premiumAmount'?: string;
    'rider.coverageAmount'?: string;
}

export interface IGTMAppRiderDisplay extends IGTMApplication {
    'stepName': string;
    'rider.productCode': string;
    'rider.premiumAmount': string;
    'rider.coverageAmount': string;
}

export interface IGTMFieldError extends IGTMAdvancedApplication {
    fieldName: string;
    errorValue: string;
}

export interface IGTMErrorBanner extends IGTMAdvancedApplication {
    errorMessage: string;
}

export interface IGTMPayload<T> {
    dataLayer: T;
}

export interface IAnalyticsProgressItemListClick {
    fieldName: string;
    fieldAction: string;
    stepName: string;
    channel: string;
    productCode?: string;
    premiumAmount?: string;
    coverageAmount?: string;
}

export interface IGTMPixelFire extends IGTMApplication {
    spotName: string;
    productCode: string;
}
